<template>
  <div class="occupancy-calendar">
    <!-- Non-modal on purpose: the embedded calendar (Mobiscroll) portals its
         filter/search popups to <body>, outside the dialog content. A modal
         dialog locks body pointer events and traps focus, which makes those
         popups unclickable. The dialog is full-screen and already prevents
         every outside-dismiss interaction, so the modal lock buys nothing. -->
    <SDialog v-model:open="dialogVisible" :modal="false">
      <SDialogContent
        :show-close-button="false"
        class="occupancy-calendar !fixed !inset-0 !top-0 !left-0 !w-screen !h-screen !max-w-none !rounded-none !translate-x-0 !translate-y-0 !p-0 !border-0 !gap-0 !grid-rows-[auto_auto_1fr] overflow-y-auto overflow-x-hidden [&>*]:min-w-0"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
        @interact-outside.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("occupancy-calendar.dialog-header") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="customized-header d-flex flex-row fl-s-b">
          <div class="header-title d-flex fl-col fl-j-c">
            <p>{{ $t("occupancy-calendar.dialog-header") }}</p>
          </div>
          <div class="close-button d-flex fl-col fl-j-c" @click="close">
            <SvgIcon
              :iconContent="closeIconContent"
              :iconAlt="closeIconAlt"
              :iconStyle="closeIconStyle"
            />
          </div>
        </div>
        <div class="customized-body">
          <NoAccessEmpty v-if="!getHasAccess" />
          <smeetz-calendars
            v-else-if="showCalendar && dialogVisible"
            :userId="getUserId"
            :userGraphqlToken="getUserGraphqlToken"
            :userTableKey="getOccupancyTableKey"
            :searchServiceType="2"
            :userToken="getUserToken"
            :groupId="String(getGroupId)"
            :env="env"
            :lang="getLanguage()"
            :filters="[
              { key: 'group_access', option: 12, value: Number(getGroupId) },
            ]"
            height="92vh"
            appType="pos"
            @edit-button:clicked="onCalendar($event, 'edit-button:clicked')"
          />
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from "vue";
import { ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { useI18n } from "vue-i18n";
import ModalModule from "@/store/modules/modal";
import BookingModule from "@/store/modules/booking";
import closeIcon from "@/assets/images/svg/close.svg";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import NoAccessEmpty from "@/components/common/NoAccessEmpty.vue";
import AuthModule from "@/store/modules/auth";
import { getProductCategoryById } from "@/dexie/product-category";
import { PCTypes } from "@/types/db/product-category";
import CustomersModule from "@/store/modules/customers";
import { BookingStep } from "@/constants/booking";
import { ISynchingReason } from "@/interfaces/store/IBooking";
import { cloneDeep } from "lodash";
import { pullAndStorePCs } from "@/utils/vue-helpers/product-category";
import {
  selectBooking,
  toLocalProductSlotBooking,
} from "@/utils/vue-helpers/booking";
import { POSConfigErrors } from "@/constants/errors/pos-config";
import { NetworkErrors } from "@/constants/errors/network";
import {
  getUserAccessToken,
  getSmtzAuth,
  getSmtzAuthFromStorage,
  showSessionExpiredPopup,
} from "@/utils/helpers/auth";
import { getLanguage } from "@/utils/helpers/lang";

// @ts-ignore
import { SmeetzCalendars } from "/node_modules/@smeetz/calendars/dist/calendars.js";
import "/node_modules/@smeetz/calendars/dist/style.css";
import AppModule from "@/store/modules/app";
import PSSModule from "@/store/modules/pss";
import { addPSSExtensions } from "@/utils/vue-helpers/orders";
import { hasSalesStarted } from "@/utils/vue-helpers/cash-float";
import i18n from "@/lang";
import { createOrderSession } from "@/utils/helpers/product-slot-booking";

import {
  countPSSByBookingId,
  getAccountingEntiesByListOfAccountingIds_Paginated,
  getBookingInfo,
  getFullBookingById,
  getOtherTypesAccountingsByBookingId,
  getPSSBookingId,
  getTicketTypeAccountingsByBookingIdWithAccEntries_Paginated,
} from "@/api/gql/product-slot-boooking";
import dayjs from "dayjs";
import { upsertUntypedBooking } from "@/db/untyped-booking";
import { IUntypedBooking, SyncStatus } from "@/types/db/product-slot-booking";
import { createCompositeId } from "@/utils/helpers/app";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "OccupancyCalendar",
  components: {
    SmeetzCalendars,
    NoAccessEmpty,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  async setup() {
    const { t } = useI18n({});
    const closeIconStyle =
      "width: 30px; height: 30px; filter: contrast(170%) invert(100%); cursor: pointer;";
    const closeIconContent = closeIcon;
    const closeIconAlt = "Close";
    const showCalendar = ref<boolean>(false);

    // The occupancy engine is granted to manager-level roles only. A seller
    // gets a null token, so the calendar shows the no-access panel instead.
    const getHasAccess = computed(
      () => !!UserModule.getUser?.elasticSearchTokens?.occupancy
    );

    onMounted(async () => {
      if (!getHasAccess.value) {
        return;
      }
      // The embedded calendar authenticates its own GraphQL calls with the
      // smtz-auth token we pass as a prop and does not refresh it itself, so we make
      // sure we hold a valid one before mounting. getSmtzAuth() is the single
      // source of truth: it returns the cached token or transparently refresh an
      // expired one.
      try {
        await getSmtzAuth();
        showCalendar.value = true;
      } catch (error) {
        LogService.info(
          "Could not obtain a valid smtz auth token to open the occupancy calendar",
          { error },
          LogLevel.Error
        );
        if (
          error instanceof Error &&
          error.message === NetworkErrors.NoAccessToken
        ) {
          // No session at all: route the user to re-login.
          showSessionExpiredPopup();
        } else if (!AuthModule.showingSessionExpiryPopup) {
          // A 401/403 while re-minting already triggered the session-expired flow
          // through the shared request interceptor; for anything else (offline,
          // backend issue) show the classified message and stay signed in.
          NotificationService.showCustomMessage(
            handleErrorAndPrepareMessage(error).errorMessage,
            "error"
          );
        }
      }
    });

    const dialogVisible = dialogVisibleForStatus(ModalStatus.OccupancyCalendar);
    const getOccupancyTableKey = computed(() => {
      return UserModule.getUser?.elasticSearchTokens?.occupancy;
    });
    const getUserToken = computed(() => {
      return AuthModule.token || getUserAccessToken();
    });
    const getGroupId = computed(() => {
      const groupId = String(POSConfigModule.groupId);

      return groupId;
    });
    const getUserId = computed(() => {
      return String(UserModule.userId);
    });

    const getUserGraphqlToken = computed(() => {
      return getSmtzAuthFromStorage();
    });
    const env = import.meta.env.VITE_ENV;

    function onCalendar(value: any, type: any) {
      if (
        value &&
        value._rawData &&
        value._rawData.occupancy_booking_reference &&
        value._rawData.occupancy_booking_reference.raw
      ) {
        const bookingReference = value._rawData.occupancy_booking_reference.raw;
        // Lets first check if the order that the user has clicked on is already in the localStorage
        // If it is, we will just open the order tab without repulling
        const bookingInState = BookingModule.bookingList.find(
          (booking) => booking.reference === bookingReference
        );
        if (bookingInState) {
          // We will manually select the booking just because the booking can be in the hidden bookings array
          console.log("going to manually select the booking");
          selectBooking(bookingInState.uuid, { fromMoreOrders: true });
          // Lets open the order tab
          console.log("setting animation true");
          BookingModule.setTabAnimation(true);
          // lets now close orders table
          this.close();
          return;
        }
      }

      this.selectOrderForEditing(value.occupancyBookingId);
      console.log(type, " - ", value.occupancyBookingId);
    }

    return {
      t,
      dialogVisible,
      onCalendar,
      getOccupancyTableKey,
      getUserToken,
      getGroupId,
      getLanguage,
      getUserGraphqlToken,
      getUserId,
      showCalendar,
      getHasAccess,
      env,
      closeIconStyle,
      closeIconContent,
      closeIconAlt,
    };
  },
  methods: {
    close() {
      ModalModule.setStatus(ModalStatus.Hidden);
    },
    async selectOrderForEditing(id = "123456") {
      if (!hasSalesStarted.value) {
        // do not pull orders if there isn't a sales session open
        NotificationService.showCustomInfoWithTitle(
          this.t("common.open-sales-before-edit"),
          ""
        );
        return;
      }
      const openTeamsView = false;
      const selectedOrderId = id;
      // Show a spinner telling the user to wait till we pull the booking
      // TODO Hamza: now you're showing the spinner in BaseLayout.vue/el-container element and you dynamically override one element z-index,
      // what would be better is attaching the spinner here in Orders.vue with a customized text
      // Try again when you have time
      AppModule.updatedIsLoading(true);

      try {
        LogService.info(
          `Going to pull the booking with id: ${selectedOrderId} from backend`
        );
        //// Pulling large bookings logic start
        // TODO Hamza: Count PSS from remote to see if it is a large booking
        // if pssCount > 550 then we consider this booking as a large booking
        // NB: Graphql support a response of 600 nested objects

        const pssCount = await countPSSByBookingId(selectedOrderId);
        const isLargeBooking = pssCount && pssCount > 559 ? true : false;
        // isLargeBooking = await countPSSByBookingId(selectedOrderId) > 550;
        // countPSSByBookingId: ProductSlotStateCount(where:"(booking_id,eq,7256454)")
        let pulledBooking: any = null;
        if (isLargeBooking) {
          let booking: any = null;
          const bookingId = selectedOrderId;
          try {
            console.log("calling getBookingInfo");
            booking = await getBookingInfo(bookingId);

            try {
              console.log("calling getPSSBookingId");
              booking.pssList = await getPSSBookingId(bookingId);
            } catch (err) {
              console.log("Error when pulling large booking");
              console.log(err);
            }

            const bookingAccountings: any[] = [];
            try {
              console.log(
                "calling getTicketTypeAccountingsByBookingIdWithAccEntries_Paginated"
              );
              // lets concat the result of the below call to the bookingAccountings
              bookingAccountings.push(
                ...((await getTicketTypeAccountingsByBookingIdWithAccEntries_Paginated(
                  bookingId
                )) || [])
              );
            } catch (err) {
              console.log(
                "Error when pulling paginated TICKET type acountings"
              );
              console.log(err);
            }
            try {
              console.log("calling getOtherTypesAccountingsByBookingId");
              // lets concat the result of the below call to the bookingAccountings
              bookingAccountings.push(
                ...((await getOtherTypesAccountingsByBookingId(bookingId)) ||
                  [])
              );
            } catch (err) {
              console.log("Error when pulling other type accountings");
              console.log(err);
            }
            const accountingIds: string[] = bookingAccountings.map(
              (acc) => acc.id
            );
            const bookingAccountingsEntries: any[] = [];
            try {
              console.log(
                "calling getAccountingEntiesByListOfAccountingIds_Paginated"
              );
              // lets concat the result of the below call to the bookingAccountingsEntries
              bookingAccountingsEntries.push(
                ...((await getAccountingEntiesByListOfAccountingIds_Paginated(
                  accountingIds
                )) || [])
              );
            } catch (err) {
              console.log("Error when pulling paginated accounting entries");
              console.log(err);
            }
            // Lets now fill each accounting with its accountingEntries
            for (let i = 0; i < bookingAccountings.length; i++) {
              const accounting = bookingAccountings[i];
              const accountingEntries: any[] = [];
              for (let j = 0; j < bookingAccountingsEntries.length; j++) {
                const accountingEntry = bookingAccountingsEntries[j];
                if (accountingEntry.accountingId === accounting.id) {
                  accountingEntries.push(accountingEntry);
                }
              }
              bookingAccountings[i].accountingEntriesList = accountingEntries;
            }
            // Lets now assing the accountings to the booking
            booking.accountings = bookingAccountings;
            console.log("------------- large booking");
            console.log(booking);
            pulledBooking = booking;
          } catch (err) {
            console.log("Error when pulling large booking");
            console.log(err);
          }
        } else {
          pulledBooking = await getFullBookingById(selectedOrderId);
        }

        //// Pulling large bookings logic end

        LogService.info(
          `We pulled the attached booking from backend, id: ${pulledBooking?.id}`,
          {
            params: pulledBooking,
          }
        );
        // If no pulled order then show general error to the user
        if (!pulledBooking) {
          LogService.info(
            `No booking with id: ${selectedOrderId} was pulled from backend`,
            undefined,
            LogLevel.Error
          );
          NotificationService.showGeneralResponseError();
        }

        // Check if PSS is linked to PSC that is not linked to the selected Config ticketGroups
        let categoryIds: number[] =
          this.getPSCNotIncludedInConfig(pulledBooking);
        console.log("--------- categoryIds");
        console.log(categoryIds);
        // categoryIds = [43924]; //Snack 1, just to test
        if (categoryIds.length) {
          // pull these PSC from remote and store them in indexedDB
          LogService.info(
            `Going to pull PCs with  attached ids for Edit Order since they do not exist in current config`,
            {
              params: categoryIds,
            }
          );
          console.log(
            "------------- Going to pull PCs with  attached ids for Edit Order since they do not exist in current config"
          );
          const missingPCs = await pullAndStorePCs(categoryIds);
        }
        // So user has access right to this order, lets convert it into IPSB type and store it in the state
        const convertedBooking = await toLocalProductSlotBooking(pulledBooking);
        LogService.info(
          `We converted pulled booking into IPSB format, result object attached`,
          {
            params: convertedBooking,
          }
        );

        // Foreach seating PSS, if it is none-reccuring seating plan, then you need to pull its eventKey from childPSC.seatingParentResourceEventKey
        for (let i = 0; i < convertedBooking.pssList.length; i++) {
          const childPSC = await getProductCategoryById(
            convertedBooking.pssList[i].categoryId
          );
          if (childPSC) {
            // Lets check if this is a childPSC of a reccurring parent
            if (!childPSC.isParentReccuringSeating) {
              // so this pss is not a recurring seating plan, lets override the eventKey value of this pss
              convertedBooking.pssList[i].eventKey =
                childPSC.seatingParentResourceEventKey;
            }
          }
        }
        // End of eventKey changes

        // Lets store it in bookingModule (bookings, bookingList, bookingsSteps) and consider this booking as the selected booking
        const bookingUUID = convertedBooking.uuid as string;
        BookingModule.addBooking(convertedBooking);

        // If the pulled booking is of type Acompte, then we show the user the payment step (he can always go back)
        // If the pulled booking is a normal booking, then we put the user in order step
        if (openTeamsView) {
          BookingModule.setBookingStep({
            bookingUUID,
            step: BookingStep.Teams,
          });
        } else if (BookingModule.isAcompteBooking) {
          BookingModule.setBookingStep({
            bookingUUID,
            step: BookingStep.Payment,
          });
        } else {
          BookingModule.setBookingStep({
            bookingUUID,
            step: BookingStep.Order,
          });
        }

        // Now lets set the customer if we have any
        const customer = pulledBooking?.CustomerRead;
        if (convertedBooking.customerId && customer) {
          const {
            country,
            email,
            phone,
            id,
            company,
            address,
            allergies,
            language,
            firstName,
            lastName,
            title,
            zipCode,
            emailMarketingOrganiser,
            birthDate,
            city,
          } = customer;

          // Lets show the customer of this booking in the UI
          await CustomersModule.setSelectedCustomers([
            {
              fullName: firstName + " " + lastName,
              bookingUuid: bookingUUID,
              checkOrder: true,
              country: country?.name || "",
              email,
              phone,
              id,
              company,
              address,
              allergies,
              language,
              firstName,
              lastName,
              title,
              zipCode,
              emailMarketingOrganiser,
              birthDate,
              city,
            },
          ]);
        }

        // For each pss lets add its extension
        let pssExtensionsCopy = cloneDeep(PSSModule.pssExtensions);
        for (const pss of convertedBooking.pssList) {
          // lets get PC data so that we store it along with the pss in pssExtensions
          // lets not forget that donation pss don't have pc in local DB
          if (pss.type !== PCTypes.Donation) {
            const pc = await getProductCategoryById(pss.categoryId);
            if (!pc) {
              LogService.info(
                `Error PC not found in local DB after pulling the booking, pssId: ${pss.id} from backend`,
                undefined,
                LogLevel.Error
              );
              continue;
            }
            pssExtensionsCopy = cloneDeep(
              addPSSExtensions({
                pssExtensions: pssExtensionsCopy,
                dt: [{ psc: pc, pss }],
              })
            );
          } else {
            // We are in a donation pss case
            // we know we don't have its PC in localDB
            pssExtensionsCopy = cloneDeep(
              addPSSExtensions({
                pssExtensions: pssExtensionsCopy,
                dt: [{ psc: null, pss }],
              })
            );
          }
        }

        PSSModule.setPSSExtensions(pssExtensionsCopy);

        // save booking to indexedDB
        const creationTime = dayjs().toISOString();
        const untypedBooking: IUntypedBooking = {
          _id: String(convertedBooking.uuid),
          uuid: String(convertedBooking.uuid),
          creationDateTime: creationTime,
          booking: convertedBooking,
          syncStatus: SyncStatus.Synced, // as it came from backend
          attempts: 0,
        };
        await upsertUntypedBooking(untypedBooking);

        // Create an order session for the booking after inserting it in Dexie
        // This is in order to avoid it being considered an update instead of a new order session
        const orderSession = createOrderSession({
          bookingUuid: convertedBooking.uuid as string,
          currency: convertedBooking.currency,
          sellerId: UserModule.userId as number,
          sellerCogn: AuthModule.cognitoId,
        });
        orderSession.bookingId = convertedBooking.id;

        BookingModule.addOrderSessionToBooking({
          orderSession,
          bookingUuid: bookingUUID,
        });

        BookingModule.selectBooking(bookingUUID);
      } catch (err: any) {
        const errorDetails = handleErrorAndPrepareMessage(err);
        console.log("errorDetails");
        console.log(errorDetails);
        LogService.info(
          `${errorDetails.errorType} Error when pulling booking with id: ${selectedOrderId} from backend`,
          { params: { err: err, errorDetails: errorDetails } },
          LogLevel.Error
        );
        NotificationService.showCustomMessage(
          errorDetails.errorMessage,
          "error"
        );
        // console.log(err);
        // console.log(Object.keys(err));
        // console.log(err.message);
        // console.log(err.graphQLErrors);
        // console.log(err.networkError);
        // console.log(err.extraInfo);
      }
      ModalModule.setStatus(ModalStatus.Hidden);
      AppModule.updatedIsLoading(false);
    },
    getPSCNotIncludedInConfig(pulledBooking: any) {
      console.log("-------- hi from getPSCNotIncludedInConfig");
      const categoryIds: number[] = [];
      if (!POSConfigModule.getSelectedConfig) {
        console.log(`----- throwing ${POSConfigErrors.NoPOSConfigSelected}`);
        throw new Error(POSConfigErrors.NoPOSConfigSelected);
      }
      console.log(
        "----------- no error when getting POSConfigModule.getSelectedConfig"
      );
      const selectedConfigTicketGroups: number[] =
        POSConfigModule.getSelectedConfig.ticketGroups;
      console.log("----------- selectedConfigTicketGroups");
      console.log(selectedConfigTicketGroups);

      const pssList = pulledBooking.pssList;
      console.log("----------- pssList");
      console.log(pssList);
      for (let i = 0; i < pssList.length; i++) {
        const psc = pssList[i].ProductSlotCategoryRead;
        console.log("----------- psc");
        console.log(psc);
        const pscTicketGroups = psc.TicketGroupTicketList.map(
          (tg: any) => tg.TicketGroupId
        );
        console.log("----------- pscTicketGroups");
        console.log(pscTicketGroups);
        if (!pscTicketGroups || !pscTicketGroups.length) {
          // So this psc has no ticketGroup, it means this psc doesn't belong to any config, it means we should consider it to be pulled anyway
          console.log(
            "----------- So this psc has no ticketGroup, it means this psc doesn't belong to any config, it means we should consider it to be pulled anyway"
          );
          categoryIds.push(psc.Id);
        }

        // If at least one tgId of the PSC is not included in selectedConfigTicketGroups then we add psc.Id to categoryIds
        for (let j = 0; j < pscTicketGroups.length; j++) {
          const index = selectedConfigTicketGroups.findIndex(
            (it) => it === pscTicketGroups[j]
          );
          if (index < 0) {
            // This psc ticketGroupId is not included in the currently selected config, lets add this categoryId to categoryIds array
            const pscId = pssList[i].ProductSlotCategoryRead.Id;
            categoryIds.push(pscId);
            break;
          }
        }
        console.log("----------- done from pscTicketGroups for loop");
      }
      console.log("----------- done from pssList for loop");
      console.log(
        "----------- going to return categoryIds from getPSCNotIncludedInConfig"
      );
      console.log(categoryIds);
      return categoryIds;
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.occupancy-calendar {
  .customized-header {
    padding-left: 30px;
    padding-right: 30px;
    background-color: #004c53;
    height: 45px;
    color: white;
    position: sticky;
    top: 0;
    z-index: 2005; // Greater than element-plus dialog z-index value which is 2003

    .header-title p {
      font-size: 18px;
      font-weight: 700;
    }

    .go-back-icon {
      margin-right: 15px;
    }
  }

  .customized-body {
    .order-details {
      height: 100%;
      .progressbar-wrapper {
        height: 125px;
        width: 40%;
        margin-left: auto;
        margin-right: auto;
        margin-top: 40px;
        margin-bottom: 40px;
        text-align: center;
        color: #004c53;

        .img-wrapper {
          img {
            width: 200px;
            height: 137px;
          }
        }

        .loading-text {
          font-weight: 600;
          font-size: 17px;
          line-height: 19px;
          margin-bottom: 10px;
        }

        .one-sec-text {
          font-weight: 400;
          font-size: 14px;
          line-height: 16px;
          margin-top: 15px;
        }

        .el-progress-bar {
          .el-progress-bar__outer {
            height: 26px;
            border: 2px solid #004c53;
            background-color: transparent;

            .el-progress-bar__inner {
              height: 20px;
              top: 1px;
            }
          }
        }
      }
    }
  }

  .el-table__body {
    border-collapse: separate;
    border-spacing: 0 5px;
  }
  thead .el-table__cell {
    background-color: rgba(48, 170, 188, 0.1);
    color: #333333;

    .cell {
      word-break: break-word;
    }
  }

  .filters-wrapper {
    // width: fit-content;
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;
    margin-bottom: 20px;

    .filters-row {
      margin-top: 5px;
      margin-bottom: 5px;

      .sort-title {
        font-weight: 600;
        font-style: normal;
        font-size: 15px;
        text-align: center;
        line-height: 15px;
        margin-right: 10px;
      }

      .filter-button {
        width: 160px;
        height: 36px;
        font-weight: 500;
        font-style: normal;
        font-size: 14px;
        text-align: center;
        line-height: 15px;
        letter-spacing: 0px;
        margin-left: 5px;
        margin-right: 5px;
      }

      .search-input {
        border: 1px solid #dcdfe6;
        color: #606266;
        letter-spacing: 1px;
        width: 250px;
        border-radius: 4px;
      }
      .search-input:focus {
        border: 1px solid #dcdfe6;
        outline: none;
      }
      .search-input::placeholder {
        color: #dcdfe6;
        text-align: left;
        padding-left: 5px;
      }
      .search-input::-webkit-search-cancel-button {
        opacity: 0.4;
      }
    }

    .active {
      background-color: $color-pacific-blue;
      font-weight: bold;
      color: $background-color-base;
    }
  }

  .orders-table-wrapper {
    padding-top: 15px;

    .el-table-fixed-column--right {
      background-color: transparent;
    }

    .edit-order-btn {
      border: 1px solid rgb(217, 223, 229);
      padding-left: 6px;
      padding-right: 6px;
      border-radius: 4px;
      background-color: rgb(253, 254, 254);

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        height: 32px;
        line-height: 32px;
        text-align: center;
      }
    }
  }

  .link {
    text-decoration: underline;
    cursor: pointer;
    color: $color-dark-cyan;
  }
}
</style>
